import { lazy, Suspense, useCallback, useEffect, useState, type CSSProperties, type DragEvent } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { AnimatePresence, MotionConfig, motion } from 'framer-motion'
import { useDensity } from '@/lib/density'
import { CursorGlow } from '../motion/CursorGlow'
import { MagneticFx } from '../motion/MagneticFx'
import { EASE_DECEL, useTierDurations } from '../motion/transitions'
import { TelemetryBoot } from '../telemetry/useTelemetry'
import { dockReservations, workspaceStore } from '../workspace/store'
import { useWorkspaceState } from '../workspace/useWorkspace'
import { AmbientField } from './AmbientField'
import { AppBarV2 } from './AppBarV2'
import { BottomNavV2 } from './BottomNavV2'
import { ErrorBoundaryV2 } from './ErrorBoundaryV2'
import { IS_EMBED } from './migration'
import { NavRailV2 } from './NavRailV2'

// Non-critical shell overlays: NOT needed for first contentful paint, so
// they're split out of the index chunk and loaded right after paint via
// React.lazy + <Suspense fallback={null}>. The keydown handler + cmdOpen
// state below stay eager, so Cmd+K still flips state and mounts the
// palette on demand. (WorkspaceController shares useWorkspace.ts with the
// eager useWorkspaceState hook, so it doesn't actually split out — kept
// lazy here only for grouping consistency; it's a headless null render.)
const CommandPalette = lazy(() =>
  import('@/components/CommandPalette').then((m) => ({ default: m.CommandPalette })),
)
const InboxNotifications = lazy(() =>
  import('./InboxNotifications').then((m) => ({ default: m.InboxNotifications })),
)
const WorkspaceController = lazy(() =>
  import('../workspace/useWorkspace').then((m) => ({ default: m.WorkspaceController })),
)
const WorkspaceLayer = lazy(() =>
  import('../workspace/WorkspaceLayer').then((m) => ({ default: m.WorkspaceLayer })),
)
const HermesInspector = lazy(() =>
  import('../flowmap/HermesInspector').then((m) => ({ default: m.HermesInspector })),
)
const CornerCluster = lazy(() =>
  import('../hud/CornerCluster').then((m) => ({ default: m.CornerCluster })),
)

const DRAG_MIME = 'text/x-hub-path'

function StageFallback() {
  return (
    <div className="v2-stage-fallback" aria-live="polite">
      <span className="fui-eyebrow">
        <span className="fui-eyebrow-dot" aria-hidden="true" /> loading…
      </span>
    </div>
  )
}

/**
 * Bare embed shell (R-embed): when the URL carries ?embed=1 the app drops
 * ALL mission-control chrome — no rail, appbar, footer, ambient canvas,
 * bottom nav, workspace overlays or command palette — and mounts only the
 * routed page filling the viewport. It keeps exactly the providers a route
 * still needs: MotionConfig (reduced-motion backstop for in-page motion
 * like the SubNav), the stage error boundary, and a Suspense boundary for
 * the lazy route chunk. TierProvider + ModelProvider live above in
 * App/main, so they're present here too. Target: a phone-PWA iframe of
 * /agenda?embed=1 that shows just the calendar.
 */
function EmbedShell() {
  return (
    <MotionConfig reducedMotion="user">
      <div className="v2-embed">
        <ErrorBoundaryV2 scope="stage">
          <Suspense fallback={<StageFallback />}>
            <Outlet />
          </Suspense>
        </ErrorBoundaryV2>
      </div>
    </MotionConfig>
  )
}

/**
 * The V2 mission-control shell: void canvas + ambient + dot grid, FUI
 * rail, appbar, animated route stage (the "floor"), workspace overlay,
 * mobile bottom nav. Docked windows reflow the floor via the
 * --ws-*-frac vars (true split, not occlusion). MotionConfig
 * reducedMotion="user" backstops the PRM clamp (R23).
 */
function MissionControlShell() {
  const { pathname } = useLocation()
  const [cmdOpen, setCmdOpen] = useState(false)
  const { toggle: toggleDensity } = useDensity()
  const dur = useTierDurations()
  const ws = useWorkspaceState()
  const res = dockReservations(ws)

  const openCmd = useCallback(() => setCmdOpen(true), [])
  const closeCmd = useCallback(() => setCmdOpen(false), [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setCmdOpen((o) => !o)
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === 'Period') {
        e.preventDefault()
        toggleDensity()
      } else if (e.key === 'Escape' && cmdOpen) {
        setCmdOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cmdOpen, toggleDensity])

  // Nav-drag spawn: dropping a rail item on the stage opens it windowed.
  function onStageDragOver(e: DragEvent) {
    if (e.dataTransfer.types.includes(DRAG_MIME)) e.preventDefault()
  }
  function onStageDrop(e: DragEvent) {
    const path = e.dataTransfer.getData(DRAG_MIME)
    if (path) {
      e.preventDefault()
      workspaceStore.openWindow(path)
    }
  }

  const shellVars = { '--ws-left-frac': res.left, '--ws-right-frac': res.right } as CSSProperties

  return (
    <MotionConfig reducedMotion="user">
      <div className="v2-shell" style={shellVars}>
        <AmbientField />
        <div className="v2-dotgrid" aria-hidden="true" />
        <div className="v2-scanline" aria-hidden="true" />
        <NavRailV2 />
        <div className="v2-main">
          <AppBarV2 onOpenPalette={openCmd} />
          <main className="v2-stage" onDragOver={onStageDragOver} onDrop={onStageDrop}>
            <ErrorBoundaryV2 scope="stage">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={pathname}
                  className="v2-route"
                  initial={{ opacity: 0, y: 16, scale: 0.992, filter: 'blur(7px)' }}
                  animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: -8, scale: 0.996, filter: 'blur(5px)' }}
                  transition={{ duration: dur.page, ease: EASE_DECEL }}
                >
                  <Suspense fallback={<StageFallback />}>
                    <Outlet />
                  </Suspense>
                </motion.div>
              </AnimatePresence>
            </ErrorBoundaryV2>
          </main>
          <div className="v2-footer">
            tovieos · hub <span className="alive">v2</span> · mission control
          </div>
        </div>
        <TelemetryBoot />
        {/* Deferred overlays: load right after first paint, fallback={null}
            so nothing flashes and the shell paints without waiting on them. */}
        <Suspense fallback={null}>
          <InboxNotifications />
          <WorkspaceController />
          <WorkspaceLayer />
          <HermesInspector />
          <CornerCluster />
        </Suspense>
        <MagneticFx />
        <CursorGlow />
        <BottomNavV2 />
        <Suspense fallback={null}>
          <CommandPalette open={cmdOpen} onClose={closeCmd} />
        </Suspense>
      </div>
    </MotionConfig>
  )
}

/**
 * Layout-route entry. `IS_EMBED` is a stable module constant read from the
 * initial URL, so this picks one branch for the whole session — each shell
 * owns its own hooks, so there's no conditional-hook hazard.
 */
export function AppShellV2() {
  return IS_EMBED ? <EmbedShell /> : <MissionControlShell />
}
